/**
 * Pure per-turn fold for the chat turn engine (HIG-5970/HIG-5971, design:
 * docs/research/chat-engine-design-no-effect.md §2).
 *
 * `applyEvent(state, event) → [TurnState, StorePatch[]]` is the single event
 * switch for both the submit and resume paths: the hooks' mutable
 * text/thinking/contentParts/subagent accumulator maps are one immutable
 * `TurnState`, store mutations are `StorePatch` values the `MessageSink`
 * applies, and thinking-store publication is a `publishThinking` patch
 * rather than a direct write. No React, no throwing, no I/O — testable with a
 * fixture list of events. Subagent-scoped events fold through
 * `subagent-fold.ts` under the same contract.
 */
import type {
  AssistantMessage,
  ChatMessage,
  ContentPart,
  FinalizedAttachment,
  MetadataEvent,
  SubagentState,
  ToolCall,
} from '../types'
import { BUILTIN_TOOLS, getToolDisplayName, isBuiltinTool } from '../integrations/utils'
import {
  isSubagentScoped,
  type ChatSSEContentEvent,
  type ChatSSELoadingEvent,
  type ChatSSEMetadataEvent,
  type ChatSSERagCheckedEvent,
  type ChatSSERagUsedEvent,
  type ChatSSEReasoningEvent,
  type ChatSSETextEvent,
  type ChatSSEToolResponseEvent,
  type ChatSSEToolUpdateEvent,
  type ChatSSEToolUseEvent,
  type ChatSSEUsageEvent,
  type ChatUsage,
} from './events'
import { continuesText } from '../utils/continues-text'
import { faviconUrlFor } from '../utils/favicon-url'
import { parseToolInput, toolCallExtras } from './fold-utils'
import {
  applySubagentEvent,
  drainSubagentPatches,
  finalizeSubagentsForParent,
  subagentFlushPatch,
  type SubagentToolEvent,
} from './subagent-fold'
import { advanceThinkingScan, initialThinkingScan, thinkingScanResult, type ThinkingScanState } from './thinking-markup'

/**
 * Accumulated stream content for one assistant message. Field presence
 * (`undefined` vs a value) mirrors the old hook's maps: `text` is the raw
 * concatenated text deltas, `thinking` the folded reasoning, `parts` the
 * ordered content parts (text segments + tool call references).
 */
export type MessageFold = {
  readonly text?: string
  readonly thinking?: string
  readonly parts?: ReadonlyArray<ContentPart>
  /**
   * Set once `ensureSeeded` merges a store seed. The store has answered, so
   * later events skip the lookup even when the seed left a side undefined
   * (a plain-text resume has no thinking to fill).
   */
  readonly seeded?: boolean
  /**
   * Incremental inline-`<thinking>` scan over `text` — each delta scans only
   * new content instead of re-regexing the full accumulated text (R4b).
   */
  readonly thinkingScan?: ThinkingScanState
}

export type TurnState = {
  readonly chatId: string
  /** Model id stamped onto assistant envelopes (submit path). */
  readonly modelId?: string
  /**
   * The turn's user message id — streaming-metadata patches reference it and
   * `rag-used` re-stamps attachments onto it (submit path). Follows the
   * backend rename when `metadata` carries `lastUserMessageId`.
   */
  readonly userMessageId?: string
  /** Finalized attachments the turn was submitted with (base for `rag-used`). */
  readonly attachedContext?: ReadonlyArray<FinalizedAttachment>
  /**
   * Identity of the optimistic assistant placeholder (submit path). When set,
   * the turn's first assistant `addMessage` becomes an `adoptPlaceholder`
   * patch so the bubble's group key never changes across the seam.
   */
  readonly placeholderClientId?: string
  /** The message that adopted the placeholder; unset until one does. */
  readonly placeholderOwnerId?: string
  /**
   * The turn's latest assistant message. The backend sends `metadata` once
   * per model attempt, so a later one is a fallback restart, and a latest
   * message with no tool calls is the failed attempt's unfinished step: the
   * loop only continues past a step that called tools, so a finished
   * text-only step is never followed by a fallback.
   */
  readonly latestMessageId?: string
  /**
   * The text of the unfinished step a fallback just dropped. The next attempt
   * can carry its sentence on instead of restarting ("I'll pull your" →
   * " Daily Brief…"), so its first text picks it back up.
   */
  readonly abandonedStepText?: string
  readonly messages: Readonly<Record<string, MessageFold>>
  /** Subagent folds keyed `${parentToolCallId}:${subagentIndex}`. */
  readonly subagents: Readonly<Record<string, SubagentState>>
  /** parentToolCallId → id of the message containing that subagent tool call. */
  readonly subagentParents: Readonly<Record<string, string>>
  /**
   * Child-tool events for a subagent whose parent isn't registered yet, keyed
   * by parentToolCallId. They patch the parent message, so they can't be
   * emitted until the registration names it — the registration drains them.
   */
  readonly pendingSubagentTools: Readonly<Record<string, ReadonlyArray<SubagentToolEvent>>>
}

/**
 * Store content used to seed a message's fold on stream resume: the fold's
 * content fields only. The seeded flag and incremental scan state never
 * round-trip through the store.
 */
export type MessageSeed = Pick<MessageFold, 'text' | 'thinking' | 'parts'>

/**
 * One store mutation produced by the fold. Mirrors the existing store API so
 * the `MessageSink` is a dumb dispatcher; a single ordered writer applies
 * patches, rAF-coalesced in one place (`coalescing-sink.ts`, HIG-5964).
 */
export type StorePatch =
  | { readonly op: 'addMessage'; readonly chatId: string; readonly message: ChatMessage }
  | { readonly op: 'upsertMessage'; readonly chatId: string; readonly message: ChatMessage }
  | {
      readonly op: 'adoptPlaceholder'
      readonly chatId: string
      readonly clientId: string
      readonly message: AssistantMessage
    }
  | { readonly op: 'updateLastMessage'; readonly chatId: string; readonly message: Partial<ChatMessage> }
  | { readonly op: 'discardMessage'; readonly chatId: string; readonly messageId: string }
  | { readonly op: 'addToolCall'; readonly chatId: string; readonly messageId: string; readonly toolCall: ToolCall }
  | {
      readonly op: 'updateToolCall'
      readonly chatId: string
      readonly messageId: string
      readonly toolCallId: string
      readonly updates: Partial<ToolCall>
    }
  | { readonly op: 'setStreamingMetadata'; readonly metadata: MetadataEvent }
  | {
      readonly op: 'addMessageLoader'
      readonly chatId: string
      readonly messageId: string
      readonly text: string
      readonly ephemeral: boolean
    }
  | { readonly op: 'removeMessageLoader'; readonly chatId: string; readonly messageId: string }
  | { readonly op: 'publishThinking'; readonly messageId: string; readonly text: string }
  | { readonly op: 'setChatUsage'; readonly chatId: string; readonly usage: ChatUsage }

export type TurnStateInit = {
  readonly modelId?: string
  readonly userMessageId?: string
  readonly attachedContext?: ReadonlyArray<FinalizedAttachment>
  readonly placeholderClientId?: string
}

export function initialTurnState(chatId: string, init: TurnStateInit = {}): TurnState {
  return {
    chatId,
    messages: {},
    subagents: {},
    subagentParents: {},
    pendingSubagentTools: {},
    ...init,
  }
}

/** The assistant message an event folds into, when its fold may need seeding. */
function seedTargetFor(event: ChatSSEContentEvent): string | undefined {
  if (isSubagentScoped(event)) return undefined
  switch (event.type) {
    case 'reasoning':
    case 'text':
    case 'toolUse':
      return event.messageId
    default:
      return undefined
  }
}

/**
 * Seed the event's target fold from store content before folding — required
 * for stream resume: the prior session's fold is gone, so without this the
 * first replayed delta would clobber existing content via upsert, and the
 * new-message check would duplicate the assistant message. Lazy: `seedFor` is
 * consulted until it first returns store content, and only sides still
 * missing are filled. An `undefined` seed (message not in the store yet)
 * keeps it consulted, so a history refetch landing after connect still seeds.
 */
export function ensureSeeded(
  state: TurnState,
  event: ChatSSEContentEvent,
  seedFor: (messageId: string) => MessageSeed | undefined,
): TurnState {
  const messageId = seedTargetFor(event)
  if (messageId === undefined) return state
  const fold = state.messages[messageId] ?? {}
  if (fold.seeded || (fold.text !== undefined && fold.thinking !== undefined)) return state

  const seed = seedFor(messageId)
  if (!seed) return state
  const next: MessageFold = {
    ...(fold.text === undefined && typeof seed.text === 'string' ? { text: seed.text } : {}),
    ...(fold.thinking === undefined && typeof seed.thinking === 'string' ? { thinking: seed.thinking } : {}),
    ...(fold.parts === undefined && seed.parts ? { parts: seed.parts } : {}),
    ...fold,
    seeded: true,
  }
  return { ...state, messages: { ...state.messages, [messageId]: next } }
}

function withFold(state: TurnState, messageId: string, fold: MessageFold): TurnState {
  return { ...state, messages: { ...state.messages, [messageId]: fold } }
}

function parseJsonSafe(value: string): unknown {
  try {
    return JSON.parse(value)
  } catch {
    return undefined
  }
}

/**
 * The fold's visible response text — raw accumulated text minus any inline
 * `<think>` markup. Emitters that rebuild the envelope outside `applyText`
 * (tool use, reasoning) must publish this, not `fold.text`, or a later event
 * would overwrite a cleanly stripped message with raw markup. Falls back to a
 * fresh scan when the fold has none yet (e.g. a resume seed's raw text).
 */
function foldResponse(fold: MessageFold): string {
  const text = fold.text ?? ''
  if (!text) return text
  const scan = advanceThinkingScan(fold.thinkingScan ?? initialThinkingScan, text)
  return thinkingScanResult(scan, text).response
}

function assistantEnvelope(state: TurnState, messageId: string, content: string): AssistantMessage {
  return {
    role: 'assistant',
    content,
    conversation_id: state.chatId,
    id: messageId,
    given_feedback: null,
    ...(state.modelId !== undefined ? { model_id: state.modelId } : {}),
  }
}

/**
 * Emit an assistant message: an upsert for a known message, otherwise an add —
 * except the turn's first new assistant message when an optimistic placeholder
 * is registered, which adopts it in a single op (no remove+add flicker frame).
 */
function emitAssistant(state: TurnState, message: AssistantMessage, isNew: boolean): [TurnState, StorePatch] {
  const opened = state.latestMessageId === message.id ? state : { ...state, latestMessageId: message.id }
  if (!isNew) return [opened, { op: 'upsertMessage', chatId: state.chatId, message }]
  if (state.placeholderClientId !== undefined && state.placeholderOwnerId === undefined) {
    return [
      { ...opened, placeholderOwnerId: message.id },
      { op: 'adoptPlaceholder', chatId: state.chatId, clientId: state.placeholderClientId, message },
    ]
  }
  return [opened, { op: 'addMessage', chatId: state.chatId, message }]
}

/**
 * Drop the failed attempt's unfinished step, if the latest message is one.
 * When it owned the placeholder, the row reverts to it (`releaseAssistantRow`)
 * and the next attempt re-adopts it, so the group key never changes.
 */
function abandonUnfinishedStep(state: TurnState): [TurnState, ReadonlyArray<StorePatch>] {
  const messageId = state.latestMessageId
  if (messageId === undefined) return [state, []]
  if (state.messages[messageId]?.parts?.some((part) => part.type === 'tool_call')) return [state, []]
  const messages = { ...state.messages }
  delete messages[messageId]
  const abandoned = state.messages[messageId]
  const next: TurnState = {
    ...state,
    messages,
    latestMessageId: undefined,
    abandonedStepText: abandoned ? foldResponse(abandoned) : undefined,
    ...(state.placeholderOwnerId === messageId ? { placeholderOwnerId: undefined } : {}),
  }
  return [
    next,
    [
      { op: 'discardMessage', chatId: state.chatId, messageId },
      { op: 'removeMessageLoader', chatId: state.chatId, messageId },
    ],
  ]
}

function applyMetadata(state: TurnState, event: ChatSSEMetadataEvent): [TurnState, ReadonlyArray<StorePatch>] {
  // A differing conversation id is the temp→real rename of a new chat: the
  // fold adopts it so every later patch lands under the real id. The store
  // migration itself (moving messages, id mapping, history invalidation) is a
  // facade concern handled through the pipeline's event tap before this fold.
  const chatId = event.conversationId ?? state.chatId
  const [restarted, abandonPatches] = abandonUnfinishedStep(state)
  const patches: Array<StorePatch> = [...abandonPatches]

  if (event.lastUserMessageId) {
    // Rename the optimistic user message to its backend id. A submit turn
    // knows the bubble's client id and targets it precisely: a promptless
    // continuation (tool result, auto follow-up) seeds no bubble, and the
    // role fallback would stamp the backend id onto the *previous* user
    // message instead (HIG-6215). Resume turns don't know it and keep the
    // fallback — the local id no longer matches anything.
    patches.push({
      op: 'updateLastMessage',
      chatId,
      message: {
        role: 'user',
        id: event.lastUserMessageId,
        ...(state.userMessageId !== undefined ? { clientId: state.userMessageId } : {}),
      },
    })
  }

  const metadata: MetadataEvent = {
    type: 'metadata',
    conversation_id: chatId,
    message_id: state.userMessageId ?? event.messageId ?? '',
    model: event.model,
    llm_provider: event.llmProvider,
  }
  patches.push({ op: 'setStreamingMetadata', metadata })

  const next: TurnState = {
    ...restarted,
    chatId,
    ...(event.lastUserMessageId ? { userMessageId: event.lastUserMessageId } : {}),
  }
  return [next, patches]
}

function applyReasoning(state: TurnState, event: ChatSSEReasoningEvent): [TurnState, ReadonlyArray<StorePatch>] {
  if (!event.content) return [state, []]
  const { messageId } = event
  const fold = state.messages[messageId] ?? {}
  const thinking = (fold.thinking ?? '') + event.content
  const patches: Array<StorePatch> = [{ op: 'publishThinking', messageId, text: thinking }]

  const message: AssistantMessage = {
    ...assistantEnvelope(state, messageId, foldResponse(fold)),
    thinkingContent: thinking,
    isThinkingStreaming: true,
    ...(fold.parts ? { contentParts: [...fold.parts] } : {}),
  }

  const isNew = fold.text === undefined
  const folded = withFold(state, messageId, { ...fold, ...(isNew ? { text: '' } : {}), thinking })
  const [next, patch] = emitAssistant(folded, message, isNew)
  patches.push(patch)
  return [next, patches]
}

/**
 * How text and thinking split per delta: dedicated reasoning events win;
 * otherwise an inline `<thinking>` block is extracted from the text.
 */
function foldTextThinking(
  fold: MessageFold,
  scan: ThinkingScanState,
  fullText: string,
): { thinking?: string; isStreaming: boolean; response: string } {
  const fromEvents = fold.thinking
  const { thinking: inline, response, isThinkingComplete } = thinkingScanResult(scan, fullText)
  return {
    // Dedicated reasoning events win; the inline block only fills a blank fold.
    thinking: !fromEvents && inline ? inline : fromEvents,
    // Reasoning that arrived via its own events is done once text starts.
    isStreaming: fromEvents ? false : !!(inline && inline.trim() && !isThinkingComplete),
    response,
  }
}

function textLength(parts: ReadonlyArray<ContentPart>): number {
  return parts.reduce((sum, part) => (part.type === 'text' ? sum + part.text.length : sum), 0)
}

/**
 * Fold the response text into the ordered content parts. A message is one
 * model step (the server rotates the id every step), so its text is one
 * segment, and text arriving after a tool call extends the segment before it.
 * That matches what the server stores. Each segment's text is the response
 * minus what earlier segments hold; a resumed seed can carry more than one.
 */
function foldTextParts(parts: ReadonlyArray<ContentPart> | undefined, response: string): ReadonlyArray<ContentPart> {
  const current = parts ?? []
  if (!response) return current
  let lastTextIndex = current.length - 1
  while (lastTextIndex >= 0 && current[lastTextIndex].type !== 'text') lastTextIndex--
  if (lastTextIndex === -1) {
    const newText = response.substring(textLength(current))
    return newText ? [...current, { type: 'text', text: newText }] : current
  }
  const prior = current.slice(0, lastTextIndex)
  return [...prior, { type: 'text', text: response.substring(textLength(prior)) }, ...current.slice(lastTextIndex + 1)]
}

/** The next attempt's first event decides whether it carried the dropped step on, so it consumes the text. */
function dropAbandonedStepText(state: TurnState): TurnState {
  return state.abandonedStepText === undefined ? state : { ...state, abandonedStepText: undefined }
}

function applyText(prior: TurnState, event: ChatSSETextEvent): [TurnState, ReadonlyArray<StorePatch>] {
  const { messageId } = event
  const state = dropAbandonedStepText(prior)
  const fold = state.messages[messageId] ?? {}
  const isNew = fold.text === undefined
  // A fallback attempt that carries the dropped step's sentence on keeps its
  // opening words instead of starting mid-sentence.
  const carried =
    isNew && prior.abandonedStepText && continuesText(prior.abandonedStepText, event.content)
      ? prior.abandonedStepText
      : ''
  const fullText = (fold.text ?? carried) + event.content

  const scan = advanceThinkingScan(fold.thinkingScan ?? initialThinkingScan, fullText)
  const { thinking, isStreaming, response } = foldTextThinking(fold, scan, fullText)
  const parts = foldTextParts(fold.parts, response)

  const patches: Array<StorePatch> = []
  if (thinking) {
    patches.push({ op: 'publishThinking', messageId, text: thinking })
  }

  const message: AssistantMessage = {
    ...assistantEnvelope(state, messageId, response),
    ...(thinking ? { thinkingContent: thinking } : {}),
    // Only stamped once inline markup makes the raw text diverge from the
    // extracted response — stream resume seeds the fold from it (upserts
    // merge, so re-stamping every delta keeps it current).
    ...(fullText !== response ? { rawContent: fullText } : {}),
    isThinkingStreaming: isStreaming,
    contentParts: [...parts],
  }

  const folded = withFold(state, messageId, {
    ...fold,
    text: fullText,
    ...(thinking !== undefined ? { thinking } : {}),
    parts,
    thinkingScan: scan,
  })
  const [next, patch] = emitAssistant(folded, message, isNew)
  patches.push(patch)
  return [next, patches]
}

function applyToolUse(state: TurnState, event: ChatSSEToolUseEvent): [TurnState, ReadonlyArray<StorePatch>] {
  const { messageId } = event
  const fold = state.messages[messageId] ?? {}
  const isNew = fold.text === undefined
  const text = fold.text ?? ''
  const parts: ReadonlyArray<ContentPart> = [...(fold.parts ?? []), { type: 'tool_call', toolCallId: event.toolId }]

  const message: AssistantMessage = {
    ...assistantEnvelope(state, messageId, foldResponse(fold)),
    ...(fold.thinking ? { thinkingContent: fold.thinking } : {}),
    contentParts: [...parts],
  }

  // A client tool the frontend can't run is born failed; everything else is
  // the backend's to execute. Only the outcome fields differ.
  const isBackendHandled = isBuiltinTool(event.name) || event.willSendToolResponse === true
  const toolCall: ToolCall = {
    id: event.toolId,
    toolName: event.name,
    toolInput: parseToolInput(event.input),
    metadata: event.metadata,
    view: event.view,
    ...(isBackendHandled
      ? { toolType: 'builtin', status: 'ready' }
      : { toolType: 'failure', status: 'failed', error: `Tool ${event.name} not found` }),
  }

  const folded = withFold(state, messageId, { ...fold, text, parts })
  const [next, messagePatch] = emitAssistant(folded, message, isNew)
  const patches: Array<StorePatch> = [messagePatch, { op: 'addToolCall', chatId: next.chatId, messageId, toolCall }]

  if (isBackendHandled) {
    // Ask-user calls surface their own waiting UI. search_audio_notes also
    // skips this loader because its response event never clears it.
    if (
      event.name !== BUILTIN_TOOLS.SEARCH_AUDIO_NOTES &&
      event.name !== BUILTIN_TOOLS.ASK_USER_QUESTION &&
      event.name !== BUILTIN_TOOLS.ASK_USER_FEEDBACK
    ) {
      patches.push({
        op: 'addMessageLoader',
        chatId: next.chatId,
        messageId,
        text: getToolDisplayName(event.name),
        ephemeral: true,
      })
    }
    if (event.name === BUILTIN_TOOLS.EXA_SEARCH) {
      patches.push({
        op: 'setStreamingMetadata',
        metadata: { type: 'metadata', conversation_id: next.chatId, message_id: messageId, search: true },
      })
    }
  } else {
    patches.push({ op: 'addMessageLoader', chatId: next.chatId, messageId, text: 'Tool not found', ephemeral: true })
  }

  return [next, patches]
}

/** Tools whose responses also land in the transcript as a `tool` role message. */
const TOOL_MESSAGE_TOOLS: ReadonlySet<string> = new Set([
  BUILTIN_TOOLS.EXA_SEARCH,
  BUILTIN_TOOLS.SEARCH_AUDIO_NOTES,
  BUILTIN_TOOLS.SEARCH_KNOWLEDGE,
  BUILTIN_TOOLS.SEARCH_HIGHLIGHTS,
])

type FaviconSearchResult = { favicon?: string; url?: string }

/** Top-3 result favicons from an exa_search response body, [] when unparseable. */
function exaFavicons(content: string): Array<string> | undefined {
  const parsed = parseJsonSafe(content) as { results?: Array<FaviconSearchResult> } | undefined
  if (!parsed || typeof parsed !== 'object') return undefined
  return (
    parsed.results
      ?.slice(0, 3)
      ?.map((result) => result.favicon || faviconUrlFor(result.url ?? ''))
      ?.filter(Boolean) ?? []
  )
}

function applyToolResponse(state: TurnState, event: ChatSSEToolResponseEvent): [TurnState, ReadonlyArray<StorePatch>] {
  const patches: Array<StorePatch> = []
  let next = state

  // When the parent subagent tool completes, finalize all its subagent states
  // and flush them onto the parent tool call.
  if (event.name === 'subagent') {
    next = finalizeSubagentsForParent(state, event.toolId)
    const flush = subagentFlushPatch(next, event.toolId)
    if (flush) patches.push(flush)
  }

  patches.push({
    op: 'updateToolCall',
    chatId: next.chatId,
    messageId: event.messageId,
    toolCallId: event.toolId,
    updates: { status: 'success', result: event.content, ...toolCallExtras(event) },
  })
  patches.push({ op: 'removeMessageLoader', chatId: next.chatId, messageId: event.messageId })

  if (event.name && TOOL_MESSAGE_TOOLS.has(event.name)) {
    patches.push({
      op: 'upsertMessage',
      chatId: next.chatId,
      message: {
        id: event.toolId,
        role: 'tool',
        conversation_id: next.chatId,
        toolCallId: event.toolId,
        content: event.content,
        name: event.name,
        ...(event.metadata ? { metadata: event.metadata } : {}),
        ...(event.steps ? { steps: event.steps } : {}),
      },
    })
  }

  if (event.name === BUILTIN_TOOLS.EXA_SEARCH) {
    // Favicon extraction is cosmetic — an unparseable body just skips the patch.
    const favicons = exaFavicons(event.content)
    if (favicons) {
      patches.push({
        op: 'setStreamingMetadata',
        metadata: {
          type: 'metadata',
          conversation_id: next.chatId,
          message_id: event.messageId,
          search: false,
          favicons,
        },
      })
    }
  } else if (event.name === BUILTIN_TOOLS.GENERATE_IMAGE) {
    const parsed = parseJsonSafe(event.content) as { error?: unknown } | undefined
    if (parsed && typeof parsed === 'object' && parsed.error) {
      patches.push({
        op: 'updateToolCall',
        chatId: next.chatId,
        messageId: event.messageId,
        toolCallId: event.toolId,
        updates: { status: 'failed', error: String(parsed.error) },
      })
    }
  }

  return [next, patches]
}

function applyLoading(state: TurnState, event: ChatSSELoadingEvent): [TurnState, ReadonlyArray<StorePatch>] {
  let next = state
  const patches: Array<StorePatch> = []

  // Register the parent message for a starting subagent tool call — and drain
  // everything that arrived before the registration: child tool calls, plus
  // subagent states (the old rAF flusher kept those dirty until the parent id
  // was known).
  if (event.toolName === 'subagent' && !event.loaded && event.messageId && event.toolId) {
    const registered = { ...state, subagentParents: { ...state.subagentParents, [event.toolId]: event.messageId } }
    const [drained, drainedPatches] = drainSubagentPatches(registered, event.toolId)
    next = drained
    patches.push(...drainedPatches)
  }

  // Only a start frame (`loaded: false`) marks the tool executing and shows a
  // loader. A completion frame must not revive a tool `toolResponse` already
  // finalized (success/failed + loader removed) back to in-progress.
  if (event.toolId && event.toolName && isBuiltinTool(event.toolName) && !event.loaded) {
    patches.push({
      op: 'updateToolCall',
      chatId: next.chatId,
      messageId: event.messageId,
      toolCallId: event.toolId,
      updates: { status: 'executing' },
    })
    const label =
      event.toolName === BUILTIN_TOOLS.EXA_SEARCH
        ? 'Searching...'
        : event.toolName === BUILTIN_TOOLS.GENERATE_IMAGE
          ? 'Generating image...'
          : getToolDisplayName(event.toolName)
    patches.push({
      op: 'addMessageLoader',
      chatId: next.chatId,
      messageId: event.messageId,
      text: label,
      ephemeral: false,
    })
  }

  return [next, patches]
}

function applyToolUpdate(state: TurnState, event: ChatSSEToolUpdateEvent): [TurnState, ReadonlyArray<StorePatch>] {
  const updates: Partial<ToolCall> = {
    status: 'executing',
    toolInput: parseToolInput(event.input),
    ...toolCallExtras(event),
  }
  return [
    state,
    [{ op: 'updateToolCall', chatId: state.chatId, messageId: event.messageId, toolCallId: event.toolId, updates }],
  ]
}

const RAG_CHUNK_SEPARATOR = '\n\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n\n'

function applyRagUsed(state: TurnState, event: ChatSSERagUsedEvent): [TurnState, ReadonlyArray<StorePatch>] {
  const chunks = event.chunks ?? []
  if (chunks.length === 0) return [state, []]

  const combinedKnowledgeText = chunks
    .map((chunk) => `${chunk.documentName} (Relevance: ${chunk.score.toFixed(2)})\n\n${chunk.text ?? ''}`)
    .join(RAG_CHUNK_SEPARATOR)

  const knowledgeAttachment: FinalizedAttachment = {
    type: 'selectedText',
    text: `Based on your documents (${chunks.length} ${chunks.length === 1 ? 'reference' : 'references'})\n\n${combinedKnowledgeText}`,
    // Marks rag knowledge for custom display in the context bar.
    ragProcessed: true,
  }

  return [
    state,
    [
      {
        op: 'updateLastMessage',
        chatId: state.chatId,
        message: {
          role: 'user',
          ...(state.userMessageId !== undefined ? { id: state.userMessageId } : {}),
          attached_context: [...(state.attachedContext ?? []), knowledgeAttachment],
        },
      },
    ],
  ]
}

function applyRagChecked(state: TurnState, _event: ChatSSERagCheckedEvent): [TurnState, ReadonlyArray<StorePatch>] {
  return [
    state,
    [
      {
        op: 'setStreamingMetadata',
        metadata: {
          type: 'metadata',
          conversation_id: state.chatId,
          message_id: state.userMessageId ?? '',
          knowledge_used: true,
        },
      },
    ],
  ]
}

function applyUsage(state: TurnState, event: ChatSSEUsageEvent): [TurnState, ReadonlyArray<StorePatch>] {
  if (!event.chatUsage) return [state, []]
  return [state, [{ op: 'setChatUsage', chatId: event.conversationId ?? state.chatId, usage: event.chatUsage }]]
}

/** Fold one content event into the turn, yielding the next state and its store patches. */
export function applyEvent(state: TurnState, event: ChatSSEContentEvent): [TurnState, ReadonlyArray<StorePatch>] {
  if (isSubagentScoped(event)) return applySubagentEvent(state, event)
  switch (event.type) {
    case 'metadata':
      return applyMetadata(state, event)
    case 'reasoning':
      return applyReasoning(state, event)
    case 'text':
      return applyText(state, event)
    case 'toolUse':
      // An attempt that opens with a tool call didn't carry the dropped step's sentence on.
      return applyToolUse(dropAbandonedStepText(state), event)
    case 'toolResponse':
      return applyToolResponse(state, event)
    case 'loading':
      return applyLoading(state, event)
    case 'toolUpdate':
      return applyToolUpdate(state, event)
    case 'rag-used':
      return applyRagUsed(state, event)
    case 'rag-checked':
      return applyRagChecked(state, event)
    case 'usage':
      return applyUsage(state, event)
  }
}
